import {
  useDeviceSettingsAsXmlQuery,
  useDeviceSettingsQuery
} from "generated/graphql";
import { Redirect, Route, Switch } from "polyrouter";
import React, { useEffect, useState } from "react";
import { DetailProps } from "routes/Manage/Inventory/View/DeviceDetails";
import DeviceAdvancedSettings from "../View/DeviceAdvancedSettings";
import { DeviceSettings } from "./DeviceSettings";

export const MIN_SOFTWARE_FOR_ADVANCED = "3.1";

const DeviceSettingsLoader: React.FC<{
  device: DetailProps | undefined;
  id: string;
  showAdvanced: boolean;
}> = ({ id, showAdvanced }) => {
  const [querySettings, setQuerySettings] = useState({
    noCache: false,
    pause: true,
    refreshNeeded: false,
    viewed: 0
  });
  const [xmlQuerySettings, setXMLQuerySettings] = useState({
    waitForDevice: false,
    pause: true,
    refreshNeeded: false,
    viewed: 0
  });

  const { data, fetching, executor: refreshSettings } = useDeviceSettingsQuery({
    variables: {
      id,
      noCache: querySettings.noCache
    },
    pause: querySettings.pause
  });

  const {
    data: xmlData,
    fetching: xmlFetching,
    executor: refreshXMLSettings
  } = useDeviceSettingsAsXmlQuery({
    variables: {
      id,
      waitForDevice: xmlQuerySettings.waitForDevice
    },
    pause: xmlQuerySettings.pause
  });

  /**
   * Called when either settings / advanced settings are mutated.
   * This function sorts out what the state for each of the
   * queries should be.
   *
   * The useEffect takes responsibility for performing any
   * query refreshes.
   *
   * @param initiator
   */
  const onUpdate = (initiator: string) => {
    /*
     * If basic settings were updated, then
     * 1. Ensure "noCache" flag set to true,
     * 2. Pause is set to false,
     * 3. Refresh needed is set to true
     * 4. Advanced "waitForDevice" set to false,
     * 5. Advanced pause" is set to true
     *
     * (and vice-versa if advanced settings updated)
     */
    setQuerySettings({
      ...querySettings,
      noCache: "basic" === initiator,
      pause: "basic" !== initiator,
      refreshNeeded: true // "basic" !== initiator,
    });

    setXMLQuerySettings({
      ...querySettings,
      waitForDevice: "xml" === initiator,
      pause: "xml" !== initiator,
      refreshNeeded: true // "xml" !== initiator,
    });
  };

  /**
   * Based on which setting(s) were updated (basic or advanced, aka XML),
   * performs the proper query refresh, and sets the state values so it is
   * ready to update the _other_ settings if the route is viewed.
   *
   * If this seems overly complex...
   *
   * 1. We have delicate devices that we are trying to get this information from.
   *    a. If you query them too often, they complain with errors and make you wait.
   *       A prime example is what we're trying to do here.  In a simpler world,
   *       we would just do a mutate, followed by a settings query, followed by
   *       an advanced settings query - but this will anger the device.
   * 2. We have two cache layers to consider:
   *    a. The graphql cache layer, which is invalidated in the cacehExchange
   *    b. The API layer, which is managed by toggling the "noCache" (for basic)
   *       and "waitForDevice" (for advanced) query arguments.
   *    c. Fortunately, telling EITHER basic / advanced API query to "wait for device"
   *       will update the cache for BOTH settings.  While this is a benefit, it
   *       does also create complication.
   * 3. Timing issues
   *    a. Often we don't care.  In fact, it may be 99% of the time that
   *       someone will only update basic settings, and not even look at Advanced.
   *    b. However, we MAY care, so we have to be sure to call the refresh query
   *       after performing an update.  Example:
   *       i. View Basic.  (initial query is fired)
   *       ii. View Advanced. (initial query is fired)
   *       iii. Go back to basic, save a setting.
   *       iv. Now, advanced has incorrect cached information, and IF
   *           you visit advanced, it MUST fire a refresh query
   *    c. Oh, and URQL pause only actually pauses the initial query. So, we've
   *       hijacked the prop to manually "pause" refreshes as well, so that we
   *       can control when queries are fired.
   */
  useEffect(() => {
    if (querySettings.refreshNeeded) {
      if (!querySettings.pause) {
        setQuerySettings({ ...querySettings, refreshNeeded: false });
        refreshSettings();
      }
    }

    if (xmlQuerySettings.refreshNeeded) {
      if (!xmlQuerySettings.pause) {
        setXMLQuerySettings({ ...xmlQuerySettings, refreshNeeded: false });
        refreshXMLSettings();
      }
    }
  }, [querySettings, xmlQuerySettings, refreshSettings, refreshXMLSettings]);

  return (
    <Switch>
      {showAdvanced && (
        <Route nest path="/advanced">
          <DeviceAdvancedSettings
            data={xmlData?.deviceSettingsAsXML?.settings || ""}
            loading={xmlFetching}
            onLoad={() => {
              if (xmlQuerySettings.pause) {
                setXMLQuerySettings({
                  ...xmlQuerySettings,
                  pause: false,
                  viewed: ++xmlQuerySettings.viewed
                });
              }
            }}
            onUpdate={() => {
              onUpdate("xml");
            }}
          />
        </Route>
      )}
      <Route nest path="/:category">
        <DeviceSettings
          deviceSettings={data?.deviceSettings}
          loading={fetching}
          onLoad={() => {
            if (querySettings.pause) {
              setQuerySettings({
                ...querySettings,
                pause: false,
                viewed: ++querySettings.viewed
              });
            }
          }}
          onUpdate={() => {
            onUpdate("basic");
          }}
        />
      </Route>
      <Route nest path="/">
        <Redirect
          persist={["DeviceFilterState", "InsightsFilterState"]}
          nest
          to="display"
        />
      </Route>
    </Switch>
  );
};

export default DeviceSettingsLoader;
